/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { NodeEntry, Pagination, ResultSetPaging } from '@alfresco/js-api';
import { ActivatedRoute, Params } from '@angular/router';
import {
  AlfrescoViewerModule,
  DocumentListModule,
  SearchModule,
  SearchQueryBuilderService,
  SearchSortingDefinition
} from '@alfresco/adf-content-services';
import {
  infoDrawerPreview,
  NavigateToFolder,
  SetInfoDrawerPreviewStateAction,
  SetInfoDrawerStateAction,
  SetSelectedNodesAction,
  ShowInfoDrawerPreviewAction
} from '@alfresco/aca-shared/store';
import {
  AuthenticationService,
  DataTableModule,
  NotificationService,
  PaginationModule,
  ToolbarModule,
  TranslationService,
  ViewerModule
} from '@alfresco/adf-core';
import { combineLatest, Subscription } from 'rxjs';
import {
  ContextActionsDirective,
  ContentApiService,
  InfoDrawerComponent,
  PageComponent,
  PageLayoutComponent,
  PaginationDirective,
  ToolbarComponent
} from '@alfresco/aca-shared';
import {} from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import {
  SearchInputComponent,
  DocumentListDirective,
  SearchActionMenuComponent,
  TagsColumnComponent,
  ThumbnailColumnComponent
} from '@alfresco/aca-content';
import { AlgSearchResultsRowComponent } from '../search-results-row/alg-search-results-row.component';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';

import { MatIconModule } from '@angular/material/icon';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AngularSplitModule, SplitGutterInteractionEvent } from 'angular-split';
import { AlgRenditionService } from '../../alg-rendition.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { algAuthorizationHeader } from '../../alg-auth';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatProgressBarModule,
    SearchModule,
    MatDividerModule,
    MatButtonModule,
    DocumentListModule,
    DocumentListDirective,
    ContextActionsDirective,
    DataTableModule,
    ThumbnailColumnComponent,
    SearchActionMenuComponent,
    AlgSearchResultsRowComponent,
    SearchInputComponent,
    TagsColumnComponent,
    PaginationModule,
    AlfrescoViewerModule,
    MatIconModule,
    InfoDrawerComponent,
    PaginationDirective,
    ViewerModule,
    PageLayoutComponent,
    ToolbarComponent,
    MatProgressSpinnerModule,
    AngularSplitModule,
    ToolbarModule
  ],
  selector: 'alg-search-results',
  templateUrl: './alg-search-results.component.html',
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./alg-search-results.component.scss']
})
export class AlgSearchResultsComponent extends PageComponent implements OnInit, OnDestroy {
  private readonly queryBuilder = inject(SearchQueryBuilderService);
  private readonly route = inject(ActivatedRoute);
  private readonly algRenditionService = inject(AlgRenditionService);
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthenticationService);
  private readonly translationService = inject(TranslationService);
  private readonly notificationService = inject(NotificationService);
  private readonly contentApi = inject(ContentApiService);

  infoDrawerPreview$ = this.store.select(infoDrawerPreview);

  searchedWord: string;
  queryParamName = 'q';
  data: ResultSetPaging;
  sorting = ['name', 'asc'];
  isLoading = false;
  totalResults: number;

  public previewOpened = false;
  public fileName: string;
  public blobFile: Blob = null;
  public urlMedia: string = null;
  public mimeType: string = null;
  public previewUnavailable = false;
  private previewNodeId: string | null = null;
  private previewRequest = new Subscription();
  private readonly onKeyDownBound = this.onKeyDown.bind(this);
  private readonly onKeyUpBound = this.onKeyUp.bind(this);
  private readonly onWindowBlurBound = this.onWindowBlur.bind(this);

  paneSizes: number[];
  ctrlPressed = false;

  constructor() {
    super();

    this.queryBuilder.paging = {
      skipCount: 0,
      maxItems: 25
    };

    combineLatest([this.route.params, this.queryBuilder.configUpdated])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([params, searchConfig]) => {
        // eslint-disable-next-line no-prototype-builtins
        this.searchedWord = params.hasOwnProperty(this.queryParamName) ? params[this.queryParamName] : null;
        const query = this.formatSearchQuery(this.searchedWord, searchConfig['aca:fields']);
        if (query) {
          this.queryBuilder.userQuery = decodeURIComponent(query);
        }
      });
  }

  ngOnInit() {
    const sizesString = localStorage.getItem('paneSizes');
    if (sizesString) {
      this.paneSizes = JSON.parse(sizesString);
    } else {
      this.paneSizes = [60, 40];
    }
    window.addEventListener('keydown', this.onKeyDownBound);
    window.addEventListener('keyup', this.onKeyUpBound);
    window.addEventListener('blur', this.onWindowBlurBound);
    super.ngOnInit();

    this.queryBuilder.resetToDefaults();
    this.sorting = this.getSorting();

    this.subscriptions.push(
      this.queryBuilder.executed.subscribe((data) => {
        this.queryBuilder.paging.skipCount = 0;

        this.onSearchResultLoaded(data);
        this.isLoading = false;
      }),

      this.queryBuilder.error.subscribe((err: any) => {
        this.onSearchError(err);
      })
    );

    if (this.route) {
      this.route.params.forEach((params: Params) => {
        // eslint-disable-next-line no-prototype-builtins
        this.searchedWord = params.hasOwnProperty(this.queryParamName) ? params[this.queryParamName] : null;
        if (this.searchedWord) {
          this.runQuery();
        } else {
          this.queryBuilder.userQuery = null;
          this.queryBuilder.executed.next({
            list: { pagination: { totalItems: 0 }, entries: [] }
          });
        }
      });
    }
  }

  onSearchError(error: { message: any }) {
    this.isLoading = false;
    const { statusCode } = JSON.parse(error.message).error;

    const messageKey = `APP.BROWSE.SEARCH.ERRORS.${statusCode}`;
    let translated = this.translationService.instant(messageKey);

    if (translated === messageKey) {
      translated = this.translationService.instant(`APP.BROWSE.SEARCH.ERRORS.GENERIC`);
    }

    // ACA 7.5.0 a supprime les actions NgRx Snackbar au profit de NotificationService.
    this.notificationService.showError(translated);
  }

  private isOperator(input: string): boolean {
    if (input) {
      input = input.trim().toUpperCase();

      const operators = ['AND', 'OR'];
      return operators.includes(input);
    }
    return false;
  }

  private formatFields(fields: string[], term: string): string {
    let prefix = '';
    let suffix = '*';

    if (term.startsWith('=')) {
      prefix = '=';
      suffix = '';
      term = term.substring(1);
    }

    return '(' + fields.map((field) => `${prefix}${field}:"${term}${suffix}"`).join(' OR ') + ')';
  }

  formatSearchQuery(userInput: string, fields = ['cm:name']) {
    if (!userInput) {
      return null;
    }

    if (/^http[s]?:\/\//.test(userInput)) {
      return this.formatFields(fields, userInput);
    }

    userInput = userInput.trim();

    if (userInput.includes(':') || userInput.includes('"')) {
      return userInput;
    }

    const words = userInput.split(' ');

    if (words.length > 1) {
      const separator = words.some(this.isOperator) ? ' ' : ' AND ';

      return words
        .map((term) => {
          if (this.isOperator(term)) {
            return term;
          }

          return this.formatFields(fields, term);
        })
        .join(separator);
    }

    return this.formatFields(fields, userInput);
  }

  onSearchResultLoaded(nodePaging: ResultSetPaging) {
    this.data = nodePaging;
    this.totalResults = this.getNumberOfResults();
  }

  getNumberOfResults() {
    if (this.data?.list?.pagination) {
      return this.data.list.pagination.totalItems;
    }
    return 0;
  }

  onPaginationChanged(pagination: Pagination) {
    this.queryBuilder.paging = {
      maxItems: pagination.maxItems,
      skipCount: pagination.skipCount
    };
    this.runQuery();
  }

  private getSorting(): string[] {
    const primary = this.queryBuilder.getPrimarySorting();

    if (primary) {
      return [primary.key, primary.ascending ? 'asc' : 'desc'];
    }

    return ['name', 'asc'];
  }

  onNodeDoubleClick(node: NodeEntry) {
    if (node?.entry) {
      if (node.entry.isFolder) {
        this.store.dispatch(new NavigateToFolder(node));
        return;
      }

      this.showPreview(node, { location: this.router.url });
    }
  }

  handleNodeClick(event: Event) {
    this.onNodeDoubleClick((event as CustomEvent).detail?.node);
  }

  onPreviewClosed() {
    this.store.dispatch(new ShowInfoDrawerPreviewAction());
  }

  onDrawerClosed() {
    this.store.dispatch(new SetInfoDrawerPreviewStateAction(false));
    this.store.dispatch(new SetInfoDrawerStateAction(false));
  }

  onSearchSortingUpdate(option: SearchSortingDefinition) {
    this.queryBuilder.sorting = [{ ...option, ascending: option.ascending }];
    this.runQuery();
  }

  handleSimpleClick(event: any) {
    const node = event.entry;
    if (this.ctrlPressed) {
      this.onNodeDoubleClick(event);
    } else {
      if (this.previewNodeId !== node.id) {
        this.previewRequest.unsubscribe();
        this.previewRequest = new Subscription();
        this.previewNodeId = node.id;
        this.previewUnavailable = false;
        this.blobFile = null;
        this.fileName = node.name;
        this.urlMedia = null;
        this.mimeType = null;
        this.previewOpened = true;
        if (node.content.mimeType === 'application/pdf' || node.content.mimeType.includes('image')) {
          this.previewRequest.add(
            this.fetchPdfDirectly(node.id).subscribe({
              next: (res: any) => {
                if (res) {
                  if (this.previewNodeId !== node.id) {
                    return;
                  }
                  this.fileName = node.name;
                  this.blobFile = res;
                  this.previewOpened = true;
                }
              },
              error: () => (this.previewUnavailable = true)
            })
          );
        } else if (node.content.mimeType === 'video/mp4' || node.content.mimeType.includes('video')) {
          this.previewOpened = true;
          this.fileName = node.name;
          this.mimeType = node.content.mimeType;
          this.urlMedia = this.contentApi.getContentUrl(node.id);
        } else {
          this.previewRequest.add(
            this.algRenditionService.getRendition(node.id).subscribe({
              next: (rendition: any) => {
                if (rendition.entry.status === 'CREATED') {
                  this.fetchAndDisplayPdf(node.id, node.name);
                } else {
                  this.previewRequest.add(
                    this.algRenditionService.requestPdfRendition(node.id).subscribe({
                      next: (status) => {
                        if (status === 'CREATED') {
                          this.fetchAndDisplayPdf(node.id, node.name);
                        } else {
                          this.previewUnavailable = true;
                        }
                      },
                      error: () => (this.previewUnavailable = true)
                    })
                  );
                }
              },
              error: () => (this.previewUnavailable = true)
            })
          );
        }
      }
    }
  }

  fetchAndDisplayPdf(nodeId, fileName) {
    this.previewRequest.add(
      this.algRenditionService.getRenderedPdf(nodeId).subscribe({
        next: (res: any) => {
          if (res && this.previewNodeId === nodeId) {
            this.fileName = fileName;
            this.blobFile = res;
            this.previewOpened = true;
          }
        },
        error: () => (this.previewUnavailable = true)
      })
    );
  }

  fetchPdfDirectly(nodeId) {
    const endpointUrl = window.location.origin + `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${nodeId}/content`;

    let headers = new HttpHeaders();
    const auth = algAuthorizationHeader(this.authService);
    headers = headers.append('Authorization', auth);
    headers = headers.append('Content-Type', 'application/pdf;charset=UTF-8');

    const params = {
      headers: headers,
      responseType: 'blob' as 'json'
    };

    return this.http.get(endpointUrl, params);
  }

  closePreview() {
    this.previewRequest.unsubscribe();
    this.previewRequest = new Subscription();
    this.previewNodeId = null;
    this.previewUnavailable = false;
    this.previewOpened = false;
    this.blobFile = null;
    this.fileName = null;
    this.urlMedia = null;
    this.mimeType = null;
    this.documentList.resetSelection();
    this.store.dispatch(new SetSelectedNodesAction([]));
  }

  onDragEnd(event: SplitGutterInteractionEvent) {
    // Convertir les tailles en chaîne JSON pour le stockage
    const sizesString = JSON.stringify(event.sizes);
    localStorage.setItem('paneSizes', sizesString);
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Control') {
      this.ctrlPressed = true;
    }
  }

  onKeyUp(event: KeyboardEvent) {
    if (event.key === 'Control') {
      this.ctrlPressed = false;
    }
  }

  onWindowBlur() {
    this.ctrlPressed = false;
  }

  ngOnDestroy() {
    this.previewRequest.unsubscribe();
    window.removeEventListener('keydown', this.onKeyDownBound);
    window.removeEventListener('keyup', this.onKeyUpBound);
    window.removeEventListener('blur', this.onWindowBlurBound);
    super.ngOnDestroy();
  }

  /**
   * Declenche la recherche.
   *
   * ADF 9 a supprime `queryBuilder.update()` au profit de `execute(false)`, et l'observable
   * `updated` qui portait la mise a jour du tri et de l'indicateur de chargement. On regroupe
   * ici les trois effets pour conserver exactement le comportement precedent.
   */
  private runQuery() {
    this.sorting = this.getSorting();
    this.isLoading = true;
    this.queryBuilder.execute(false);
  }
}
